import React, { useState, useEffect } from 'react'
import { Check, X, Mail, Calendar, User, AlertCircle, RefreshCw, Filter, Building, Users as UsersIcon, Package } from 'lucide-react'
import { apiClient } from '@/utils/api'
import toast from 'react-hot-toast'
import { format } from 'date-fns'

interface SignupRequest {
  id: string
  email: string
  first_name?: string
  last_name?: string
  company_name?: string
  requested_slug?: string
  requested_family_name: string
  requested_plan: string
  use_case?: string
  expected_members?: number
  status: 'pending' | 'approved' | 'rejected' | 'completed'
  status_reason?: string
  reviewed_by?: string
  reviewed_at?: string
  family_id?: string
  created_at: string
}

interface ApprovalModalProps {
  request: SignupRequest
  onClose: () => void
  onApprove: (data: ApprovalData) => void
}

interface ApprovalData {
  final_slug: string
  final_family_name: string
  final_plan: string
  is_trial: boolean
  is_preview: boolean
}

const ApprovalModal: React.FC<ApprovalModalProps> = ({ request, onClose, onApprove }) => {
  const [formData, setFormData] = useState<ApprovalData>({
    final_slug: request.requested_slug || '',
    final_family_name: request.requested_family_name,
    final_plan: request.requested_plan || 'trial',
    is_trial: true,
    is_preview: false,
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onApprove(formData)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-900">Approve Signup Request</h3>
          <p className="text-sm text-gray-600 mt-1">
            Review and configure family provisioning for {request.email}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Request Details */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <h4 className="font-medium text-gray-900">Request Details</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Email:</span>
                <span className="ml-2 font-medium">{request.email}</span>
              </div>
              {request.first_name && (
                <div>
                  <span className="text-gray-600">Name:</span>
                  <span className="ml-2 font-medium">{request.first_name} {request.last_name}</span>
                </div>
              )}
              {request.company_name && (
                <div>
                  <span className="text-gray-600">Company:</span>
                  <span className="ml-2 font-medium">{request.company_name}</span>
                </div>
              )}
              {request.expected_members && (
                <div>
                  <span className="text-gray-600">Expected Members:</span>
                  <span className="ml-2 font-medium">{request.expected_members}</span>
                </div>
              )}
            </div>
            {request.use_case && (
              <div className="mt-2">
                <span className="text-gray-600 text-sm">Use Case:</span>
                <p className="text-sm text-gray-900 mt-1">{request.use_case}</p>
              </div>
            )}
          </div>

          {/* Family Configuration */}
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">Family Configuration</h4>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Family Slug (subdomain)
              </label>
              <div className="flex items-center">
                <input
                  type="text"
                  value={formData.final_slug}
                  onChange={(e) => setFormData({ ...formData, final_slug: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '') })}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="familyname"
                  required
                />
                <span className="px-3 py-2 bg-gray-100 border border-l-0 border-gray-300 rounded-r-lg text-gray-600">
                  .housepoints.ai
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Lowercase letters and numbers only</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Family Name
              </label>
              <input
                type="text"
                value={formData.final_family_name}
                onChange={(e) => setFormData({ ...formData, final_family_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="The Smith Family"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Plan
              </label>
              <select
                value={formData.final_plan}
                onChange={(e) => setFormData({ ...formData, final_plan: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="trial">Trial (Free)</option>
                <option value="preview">Preview</option>
                <option value="premium">Premium</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>

            <div className="space-y-3">
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={formData.is_trial}
                  onChange={(e) => setFormData({ ...formData, is_trial: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900">Trial Family</span>
                  <p className="text-xs text-gray-500">1 year trial access with full features</p>
                </div>
              </label>

              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={formData.is_preview}
                  onChange={(e) => setFormData({ ...formData, is_preview: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900">Preview Access</span>
                  <p className="text-xs text-gray-500">Early access to new features</p>
                </div>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
            >
              <Check className="w-4 h-4" />
              <span>Approve & Provision</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export const SignupRequestsManager: React.FC = () => {
  const [requests, setRequests] = useState<SignupRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected' | 'completed'>('pending')
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set())
  const [approvalRequest, setApprovalRequest] = useState<SignupRequest | null>(null)

  useEffect(() => {
    loadRequests()
  }, [filter])

  const loadRequests = async () => {
    setLoading(true)
    try {
      const params = filter !== 'all' ? `?status=${filter}` : ''
      const data = await apiClient.get(`/api/platform/signup-requests${params}`)
      setRequests(data.requests || [])
    } catch (error) {
      console.error('Failed to load signup requests:', error)
      toast.error('Failed to load signup requests')
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async (requestId: string, approvalData: ApprovalData) => {
    setProcessingIds(prev => new Set(prev).add(requestId))
    try {
      const response = await apiClient.post(`/api/platform/signup-requests/${requestId}/approve`, approvalData)
      toast.success(`Family provisioned successfully! Slug: ${response.slug}`)
      setApprovalRequest(null)
      loadRequests()
    } catch (error: any) {
      console.error('Failed to approve request:', error)
      toast.error(error?.details || 'Failed to approve and provision family')
    } finally {
      setProcessingIds(prev => {
        const next = new Set(prev)
        next.delete(requestId)
        return next
      })
    }
  }

  const handleReject = async (requestId: string, email: string) => {
    const reason = prompt(`Reject signup request from ${email}?\n\nReason for rejection:`)
    if (reason === null) return // Cancelled

    if (!reason.trim()) {
      toast.error('Please provide a reason for rejection')
      return
    }

    setProcessingIds(prev => new Set(prev).add(requestId))
    try {
      await apiClient.post(`/api/platform/signup-requests/${requestId}/reject`, {
        reason: reason
      })
      toast.success(`Signup request rejected for ${email}`)
      loadRequests()
    } catch (error) {
      console.error('Failed to reject request:', error)
      toast.error('Failed to reject request')
    } finally {
      setProcessingIds(prev => {
        const next = new Set(prev)
        next.delete(requestId)
        return next
      })
    }
  }

  const getStatusBadge = (status: string) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      approved: 'bg-blue-100 text-blue-800 border-blue-200',
      rejected: 'bg-red-100 text-red-800 border-red-200',
      completed: 'bg-green-100 text-green-800 border-green-200',
    }
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${styles[status as keyof typeof styles]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    )
  }

  const pendingCount = requests.filter(r => r.status === 'pending').length

  return (
    <div>
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Family Signup Requests</h2>
            <p className="text-gray-600">Review and approve new family registrations</p>
          </div>
          <button
            onClick={loadRequests}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>

        {/* Filters */}
        <div className="flex items-center space-x-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <div className="flex space-x-2">
            {(['all', 'pending', 'approved', 'completed', 'rejected'] as const).map(status => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === status
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
                {status === 'pending' && pendingCount > 0 && (
                  <span className="ml-2 bg-white text-blue-600 px-2 py-0.5 rounded-full text-xs">
                    {pendingCount}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Pending Alert */}
      {pendingCount > 0 && filter !== 'pending' && (
        <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <AlertCircle className="w-5 h-5 text-yellow-600" />
            <span className="text-yellow-800">
              {pendingCount} pending request{pendingCount !== 1 ? 's' : ''} awaiting review
            </span>
          </div>
          <button
            onClick={() => setFilter('pending')}
            className="text-yellow-700 hover:text-yellow-900 font-medium"
          >
            View Pending
          </button>
        </div>
      )}

      {/* Requests List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : requests.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No requests found</h3>
          <p className="text-gray-600">
            {filter === 'pending'
              ? 'No pending signup requests at this time.'
              : `No ${filter} signup requests.`
            }
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Requested Family
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Plan
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Requested
                  </th>
                  {filter === 'pending' && (
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {requests.map(request => {
                  const isProcessing = processingIds.has(request.id)
                  return (
                    <tr key={request.id} className={isProcessing ? 'opacity-50' : ''}>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-blue-600" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{request.email}</div>
                            {request.first_name && (
                              <div className="text-sm text-gray-500">{request.first_name} {request.last_name}</div>
                            )}
                            {request.company_name && (
                              <div className="text-xs text-gray-500 flex items-center mt-1">
                                <Building className="w-3 h-3 mr-1" />
                                {request.company_name}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{request.requested_family_name}</div>
                        {request.requested_slug && (
                          <div className="text-xs text-gray-500">{request.requested_slug}.housepoints.ai</div>
                        )}
                        {request.expected_members && (
                          <div className="text-xs text-gray-500 flex items-center mt-1">
                            <UsersIcon className="w-3 h-3 mr-1" />
                            {request.expected_members} members expected
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm">
                          <Package className="w-4 h-4 mr-1 text-gray-400" />
                          <span className="capitalize">{request.requested_plan}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(request.status)}
                        {request.status_reason && (
                          <div className="text-xs text-gray-500 mt-1">{request.status_reason}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900">
                          <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                          {format(new Date(request.created_at), 'MMM d, yyyy')}
                        </div>
                        <div className="text-xs text-gray-500">
                          {format(new Date(request.created_at), 'h:mm a')}
                        </div>
                      </td>
                      {filter === 'pending' && (
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => setApprovalRequest(request)}
                              disabled={isProcessing}
                              className="inline-flex items-center px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Approve
                            </button>
                            <button
                              onClick={() => handleReject(request.id, request.email)}
                              disabled={isProcessing}
                              className="inline-flex items-center px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                            >
                              <X className="w-4 h-4 mr-1" />
                              Reject
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Approval Modal */}
      {approvalRequest && (
        <ApprovalModal
          request={approvalRequest}
          onClose={() => setApprovalRequest(null)}
          onApprove={(data) => handleApprove(approvalRequest.id, data)}
        />
      )}
    </div>
  )
}
